import { MentorshipDetails } from "@/lib/api/mentorships";
import { useShareableEntities } from "@/lib/api/shareable-entities";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { AddActivityButton } from "./add-activity-button";
import { HubTabs } from "./hub-tabs";
import { MentorshipCard } from "./mentorship-card";
import { EntityList } from "./entity-list";
import { useHubStore } from "./use-hub-store";
import { InfiniteScroll } from "@/components/shared/infinite-scroll";
import { cn } from "@/lib/utils";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useAbility } from "@/lib/casl/ability-context";
import { useAuth } from "@/components/auth/auth-context";
import { getMenteeDisplayFirstName } from "@/lib/redacted-display";

const HUB_TABS = [
  { id: "new", label: "New" },
  { id: "all", label: "All" },
];

interface HubListViewProps {
  mentorship: MentorshipDetails;
}

export function HubListView({ mentorship }: HubListViewProps) {
  const ability = useAbility();
  const { user } = useAuth();
  const canCreate = ability.can("create", "shareable-entities");
  const userIsMentor = user?.id === mentorship.mentor.id;

  const activeTab = useHubStore((state) => state.activeTab);
  const activeFilter = useHubStore((state) => state.activeFilter);
  const setActiveTab = useHubStore((state) => state.setActiveTab);
  const setActiveFilter = useHubStore((state) => state.setActiveFilter);
  const navigateToView = useHubStore((state) => state.navigateToView);
  const isMomentsFlag = useFeatureFlagEnabled("enable-moments");
  const isSwapsFlag = useFeatureFlagEnabled("enable-swaps");
  const isMomentsEnabled = isMomentsFlag === true;
  const isSwapsEnabled = isSwapsFlag === true;

  // The "new" tab depends on per-user read state, which only the mentor has.
  // Admin viewers see the full history instead.
  const effectiveTab = userIsMentor ? activeTab : "all";
  const readStatus = effectiveTab === "new" ? "unread" : "all";
  const filter =
    effectiveTab === "all" && activeFilter !== "all" ? activeFilter : undefined;

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useShareableEntities(mentorship.mentee.id, filter, readStatus);

  const entities = useMemo(() => {
    let allEntities = data?.pages.flatMap((page) => page.entities) ?? [];

    // Filter by moments feature flag
    if (!isMomentsEnabled) {
      allEntities = allEntities.filter(
        (entity) => entity.entityType !== "moment",
      );
    }

    // Filter by swaps feature flag
    if (!isSwapsEnabled) {
      allEntities = allEntities.filter(
        (entity) => entity.entityType !== "swap",
      );
    }

    // For "new" tab, show unread entities from the teen OR pending swaps
    if (effectiveTab === "new") {
      allEntities = allEntities.filter((entity) => {
        // Always show pending swaps in the "new" tab
        if (entity.entityType === "swap" && entity.swap?.status !== "completed") {
          return true;
        }
        return entity.isNew === true;
      });
    }

    return allEntities;
  }, [data, isMomentsEnabled, isSwapsEnabled, effectiveTab]);

  const hasSwapInProgress = useMemo(() => {
    const allEntities = data?.pages.flatMap((page) => page.entities) ?? [];
    return allEntities.some(
      (entity) =>
        entity.entityType === "swap" &&
        (entity.swap?.status === "pending_teen" ||
          entity.swap?.status === "pending_mentor"),
    );
  }, [data]);

  const handleEntityClick = (
    entityId: string,
    entityType: string,
    activityType?: string,
  ) => {
    if (entityType === "moment") {
      navigateToView("view-moment", entityId);
    } else if (entityType === "swap") {
      navigateToView("view-swap", entityId);
    } else if (
      entityType === "activity" &&
      activityType === "connect_moment"
    ) {
      navigateToView("view-connect-moment", entityId);
    } else {
      navigateToView("view-note", entityId);
    }
  };

  const menteeFirstName = getMenteeDisplayFirstName(mentorship.mentee);
  const mentorFirstName = (mentorship.mentor.name ?? "").split(" ")[0];
  const isFromMentee = activeFilter === "shared";
  const isFromMe = activeFilter === "owned";
  const isScheduled = activeFilter === "scheduled";

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-white">
      <div className="sticky top-0 z-10 bg-white">
        <div className="p-4">
          <MentorshipCard mentorship={mentorship} />
        </div>
        {userIsMentor && (
          <div className="px-4">
            <HubTabs
              tabs={HUB_TABS}
              activeTab={activeTab}
              onChangeTab={(tabId) => {
                setActiveTab(tabId as "new" | "all");
                if (tabId === "new") {
                  setActiveFilter("all");
                }
              }}
            />
          </div>
        )}
        {effectiveTab === "all" && (
          <div className="px-4 py-3">
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setActiveFilter(activeFilter === "shared" ? "all" : "shared")
                }
                className={cn(
                  "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                  isFromMentee
                    ? "bg-gray-200 text-black"
                    : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
                )}
              >
                From {menteeFirstName}
              </button>
              <button
                onClick={() =>
                  setActiveFilter(activeFilter === "owned" ? "all" : "owned")
                }
                className={cn(
                  "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                  isFromMe
                    ? "bg-gray-200 text-black"
                    : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
                )}
              >
                From {userIsMentor ? "Me" : mentorFirstName}
              </button>
              <button
                onClick={() =>
                  setActiveFilter(
                    activeFilter === "scheduled" ? "all" : "scheduled"
                  )
                }
                className={cn(
                  "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                  isScheduled
                    ? "bg-gray-200 text-black"
                    : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
                )}
              >
                Scheduled
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex min-h-[300px] flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <div className="flex min-h-[300px] flex-1 items-center justify-center">
            <div className="text-secondary">Something went wrong</div>
          </div>
        ) : (
          <>
            <EntityList
              entities={entities}
              onEntityClick={handleEntityClick}
              emptyStateTitle="No activities yet"
              emptyStateDescription="Activities will appear here once they're added."
              showNewBadges={userIsMentor}
            />
            <InfiniteScroll
              hasMore={hasNextPage ?? false}
              isLoading={isFetchingNextPage}
              onLoadMore={() => fetchNextPage()}
              className="h-4"
            />
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
          </>
        )}
      </div>
      {canCreate && userIsMentor && (
        <div className="sticky bottom-0 z-10 border-t border-gray-200 bg-white">
          <AddActivityButton hasSwapInProgress={hasSwapInProgress} />
        </div>
      )}
    </div>
  );
}
